/**
 * Generated by orval v8.2.0 🍺
 * Do not edit manually.
 * Fragment Payments API
 * REST API for Fragment payment operations
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
  CreateExternalAccountRequest,
  CreateInvoiceRequest,
  CreateProductRequest,
  CreateRoleRequest,
  CreateUserRequest,
  ErrorResponse,
  ExternalAccountListResponse,
  ExternalAccountSuccessResponse,
  InvoiceDetailSuccessResponse,
  InvoiceHistoryResponse,
  InvoiceListResponse,
  InvoiceSuccessResponse,
  PlatformSuccessResponse,
  ProductListResponse,
  ProductSuccessResponse,
  RoleListResponse,
  RoleSuccessResponse,
  SyncTransactionRequest,
  TransactionSuccessResponse,
  UpdateInvoiceRequest,
  UpdatePlatformDetailsRequest,
  UserListResponse,
  UserSuccessResponse,
} from '../../../payments-api/model';

import { usePaymentsApiHttpClient } from '../../../wrappers/PaymentsApiAuthProvider';

/**
 * Creates a new external account
 */
export const usePostExternalAccountsHook = () => {
  const postExternalAccounts = usePaymentsApiHttpClient<ExternalAccountSuccessResponse>();

  return useCallback(
    (createExternalAccountRequest: CreateExternalAccountRequest, signal?: AbortSignal) => {
      return postExternalAccounts({
        url: `/external-accounts`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createExternalAccountRequest,
        signal,
      });
    },
    [postExternalAccounts],
  );
};

export const usePostExternalAccountsMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostExternalAccountsHook>>>,
    TError,
    { data: CreateExternalAccountRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostExternalAccountsHook>>>,
  TError,
  { data: CreateExternalAccountRequest },
  TContext
> => {
  const mutationKey = ['postExternalAccounts'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postExternalAccounts = usePostExternalAccountsHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostExternalAccountsHook>>>,
    { data: CreateExternalAccountRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postExternalAccounts(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostExternalAccountsMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePostExternalAccountsHook>>>
>;
export type PostExternalAccountsMutationBody = CreateExternalAccountRequest;
export type PostExternalAccountsMutationError = ErrorResponse;

export const usePostExternalAccounts = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostExternalAccountsHook>>>,
      TError,
      { data: CreateExternalAccountRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostExternalAccountsHook>>>,
  TError,
  { data: CreateExternalAccountRequest },
  TContext
> => {
  return useMutation(usePostExternalAccountsMutationOptions(options), queryClient);
};

/**
 * Lists all external accounts for the workspace
 */
export const useGetExternalAccountsHook = () => {
  const getExternalAccounts = usePaymentsApiHttpClient<ExternalAccountListResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getExternalAccounts({
        url: `/external-accounts`,
        method: 'GET',
        signal,
      });
    },
    [getExternalAccounts],
  );
};

export const getGetExternalAccountsQueryKey = () => {
  return [`/external-accounts`] as const;
};

export const useGetExternalAccountsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetExternalAccountsQueryKey();

  const getExternalAccounts = useGetExternalAccountsHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>> = ({ signal }) =>
    getExternalAccounts(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetExternalAccountsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>
>;
export type GetExternalAccountsQueryError = ErrorResponse;

export function useGetExternalAccounts<
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetExternalAccounts<
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetExternalAccounts<
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetExternalAccounts<
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetExternalAccountsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetExternalAccountsSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<
    UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetExternalAccountsQueryKey();

  const getExternalAccounts = useGetExternalAccountsHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>> = ({ signal }) =>
    getExternalAccounts(signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetExternalAccountsSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>
>;
export type GetExternalAccountsSuspenseQueryError = ErrorResponse;

export function useGetExternalAccountsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetExternalAccountsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetExternalAccountsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetExternalAccountsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetExternalAccountsSuspenseQueryOptions(options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Creates a new invoice
 */
export const usePostInvoicesHook = () => {
  const postInvoices = usePaymentsApiHttpClient<InvoiceSuccessResponse>();

  return useCallback(
    (createInvoiceRequest: CreateInvoiceRequest, signal?: AbortSignal) => {
      return postInvoices({
        url: `/invoices`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createInvoiceRequest,
        signal,
      });
    },
    [postInvoices],
  );
};

export const usePostInvoicesMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostInvoicesHook>>>,
    TError,
    { data: CreateInvoiceRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostInvoicesHook>>>,
  TError,
  { data: CreateInvoiceRequest },
  TContext
> => {
  const mutationKey = ['postInvoices'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postInvoices = usePostInvoicesHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostInvoicesHook>>>,
    { data: CreateInvoiceRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postInvoices(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostInvoicesMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof usePostInvoicesHook>>>>;
export type PostInvoicesMutationBody = CreateInvoiceRequest;
export type PostInvoicesMutationError = ErrorResponse;

export const usePostInvoices = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostInvoicesHook>>>,
      TError,
      { data: CreateInvoiceRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostInvoicesHook>>>,
  TError,
  { data: CreateInvoiceRequest },
  TContext
> => {
  return useMutation(usePostInvoicesMutationOptions(options), queryClient);
};

/**
 * Lists all invoices for the workspace
 */
export const useGetInvoicesHook = () => {
  const getInvoices = usePaymentsApiHttpClient<InvoiceListResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getInvoices({ url: `/invoices`, method: 'GET', signal });
    },
    [getInvoices],
  );
};

export const getGetInvoicesQueryKey = () => {
  return [`/invoices`] as const;
};

export const useGetInvoicesQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetInvoicesQueryKey();

  const getInvoices = useGetInvoicesHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>> = ({ signal }) =>
    getInvoices(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetInvoicesQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>>;
export type GetInvoicesQueryError = ErrorResponse;

export function useGetInvoices<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoices<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoices<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetInvoices<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetInvoicesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetInvoicesSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetInvoicesQueryKey();

  const getInvoices = useGetInvoicesHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>> = ({ signal }) =>
    getInvoices(signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetInvoicesSuspenseQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>>;
export type GetInvoicesSuspenseQueryError = ErrorResponse;

export function useGetInvoicesSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(
  options: {
    query: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetInvoicesSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetInvoicesSuspenseQueryOptions(options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Gets an invoice by ID with balance details
 */
export const useGetInvoicesIdHook = () => {
  const getInvoicesId = usePaymentsApiHttpClient<InvoiceDetailSuccessResponse>();

  return useCallback(
    (id: string, signal?: AbortSignal) => {
      return getInvoicesId({ url: `/invoices/${id}`, method: 'GET', signal });
    },
    [getInvoicesId],
  );
};

export const getGetInvoicesIdQueryKey = (id: string) => {
  return [`/invoices/${id}`] as const;
};

export const useGetInvoicesIdQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>>;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetInvoicesIdQueryKey(id);

  const getInvoicesId = useGetInvoicesIdHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>> = ({ signal }) =>
    getInvoicesId(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetInvoicesIdQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>>;
export type GetInvoicesIdQueryError = ErrorResponse;

export function useGetInvoicesId<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesId<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesId<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetInvoicesId<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetInvoicesIdQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetInvoicesIdSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetInvoicesIdQueryKey(id);

  const getInvoicesId = useGetInvoicesIdHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>> = ({ signal }) =>
    getInvoicesId(id, signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetInvoicesIdSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>
>;
export type GetInvoicesIdSuspenseQueryError = ErrorResponse;

export function useGetInvoicesIdSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesIdSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesIdSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetInvoicesIdSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetInvoicesIdSuspenseQueryOptions(id, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Updates an invoice with line item operations
 */
export const usePostInvoicesIdHook = () => {
  const postInvoicesId = usePaymentsApiHttpClient<InvoiceSuccessResponse>();

  return useCallback(
    (id: string, updateInvoiceRequest: UpdateInvoiceRequest, signal?: AbortSignal) => {
      return postInvoicesId({
        url: `/invoices/${id}`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: updateInvoiceRequest,
        signal,
      });
    },
    [postInvoicesId],
  );
};

export const usePostInvoicesIdMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostInvoicesIdHook>>>,
    TError,
    { id: string; data: UpdateInvoiceRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostInvoicesIdHook>>>,
  TError,
  { id: string; data: UpdateInvoiceRequest },
  TContext
> => {
  const mutationKey = ['postInvoicesId'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postInvoicesId = usePostInvoicesIdHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostInvoicesIdHook>>>,
    { id: string; data: UpdateInvoiceRequest }
  > = (props) => {
    const { id, data } = props ?? {};

    return postInvoicesId(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostInvoicesIdMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof usePostInvoicesIdHook>>>>;
export type PostInvoicesIdMutationBody = UpdateInvoiceRequest;
export type PostInvoicesIdMutationError = ErrorResponse;

export const usePostInvoicesId = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostInvoicesIdHook>>>,
      TError,
      { id: string; data: UpdateInvoiceRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostInvoicesIdHook>>>,
  TError,
  { id: string; data: UpdateInvoiceRequest },
  TContext
> => {
  return useMutation(usePostInvoicesIdMutationOptions(options), queryClient);
};

/**
 * Gets the version history of an invoice
 */
export const useGetInvoicesIdHistoryHook = () => {
  const getInvoicesIdHistory = usePaymentsApiHttpClient<InvoiceHistoryResponse>();

  return useCallback(
    (id: string, signal?: AbortSignal) => {
      return getInvoicesIdHistory({
        url: `/invoices/${id}/history`,
        method: 'GET',
        signal,
      });
    },
    [getInvoicesIdHistory],
  );
};

export const getGetInvoicesIdHistoryQueryKey = (id: string) => {
  return [`/invoices/${id}/history`] as const;
};

export const useGetInvoicesIdHistoryQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetInvoicesIdHistoryQueryKey(id);

  const getInvoicesIdHistory = useGetInvoicesIdHistoryHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>> = ({ signal }) =>
    getInvoicesIdHistory(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetInvoicesIdHistoryQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>
>;
export type GetInvoicesIdHistoryQueryError = ErrorResponse;

export function useGetInvoicesIdHistory<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesIdHistory<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesIdHistory<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetInvoicesIdHistory<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetInvoicesIdHistoryQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetInvoicesIdHistorySuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetInvoicesIdHistoryQueryKey(id);

  const getInvoicesIdHistory = useGetInvoicesIdHistoryHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>> = ({ signal }) =>
    getInvoicesIdHistory(id, signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetInvoicesIdHistorySuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>
>;
export type GetInvoicesIdHistorySuspenseQueryError = ErrorResponse;

export function useGetInvoicesIdHistorySuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesIdHistorySuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesIdHistorySuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetInvoicesIdHistorySuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetInvoicesIdHistorySuspenseQueryOptions(id, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Gets platform details for the workspace
 */
export const useGetPlatformHook = () => {
  const getPlatform = usePaymentsApiHttpClient<PlatformSuccessResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getPlatform({ url: `/platform`, method: 'GET', signal });
    },
    [getPlatform],
  );
};

export const getGetPlatformQueryKey = () => {
  return [`/platform`] as const;
};

export const useGetPlatformQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPlatformQueryKey();

  const getPlatform = useGetPlatformHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>> = ({ signal }) =>
    getPlatform(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPlatformQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>>;
export type GetPlatformQueryError = ErrorResponse;

export function useGetPlatform<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>,
  TError = ErrorResponse,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPlatform<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPlatform<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetPlatform<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetPlatformQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetPlatformSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPlatformQueryKey();

  const getPlatform = useGetPlatformHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>> = ({ signal }) =>
    getPlatform(signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPlatformSuspenseQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>>;
export type GetPlatformSuspenseQueryError = ErrorResponse;

export function useGetPlatformSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>,
  TError = ErrorResponse,
>(
  options: {
    query: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPlatformSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPlatformSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetPlatformSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPlatformHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetPlatformSuspenseQueryOptions(options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Updates platform details (creates if not exists)
 */
export const usePostPlatformHook = () => {
  const postPlatform = usePaymentsApiHttpClient<PlatformSuccessResponse>();

  return useCallback(
    (updatePlatformDetailsRequest: UpdatePlatformDetailsRequest, signal?: AbortSignal) => {
      return postPlatform({
        url: `/platform`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: updatePlatformDetailsRequest,
        signal,
      });
    },
    [postPlatform],
  );
};

export const usePostPlatformMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostPlatformHook>>>,
    TError,
    { data: UpdatePlatformDetailsRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostPlatformHook>>>,
  TError,
  { data: UpdatePlatformDetailsRequest },
  TContext
> => {
  const mutationKey = ['postPlatform'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postPlatform = usePostPlatformHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostPlatformHook>>>,
    { data: UpdatePlatformDetailsRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postPlatform(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostPlatformMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof usePostPlatformHook>>>>;
export type PostPlatformMutationBody = UpdatePlatformDetailsRequest;
export type PostPlatformMutationError = ErrorResponse;

export const usePostPlatform = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostPlatformHook>>>,
      TError,
      { data: UpdatePlatformDetailsRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostPlatformHook>>>,
  TError,
  { data: UpdatePlatformDetailsRequest },
  TContext
> => {
  return useMutation(usePostPlatformMutationOptions(options), queryClient);
};

/**
 * Creates a new product
 */
export const usePostProductsHook = () => {
  const postProducts = usePaymentsApiHttpClient<ProductSuccessResponse>();

  return useCallback(
    (createProductRequest: CreateProductRequest, signal?: AbortSignal) => {
      return postProducts({
        url: `/products`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createProductRequest,
        signal,
      });
    },
    [postProducts],
  );
};

export const usePostProductsMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostProductsHook>>>,
    TError,
    { data: CreateProductRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostProductsHook>>>,
  TError,
  { data: CreateProductRequest },
  TContext
> => {
  const mutationKey = ['postProducts'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postProducts = usePostProductsHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostProductsHook>>>,
    { data: CreateProductRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postProducts(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostProductsMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof usePostProductsHook>>>>;
export type PostProductsMutationBody = CreateProductRequest;
export type PostProductsMutationError = ErrorResponse;

export const usePostProducts = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostProductsHook>>>,
      TError,
      { data: CreateProductRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostProductsHook>>>,
  TError,
  { data: CreateProductRequest },
  TContext
> => {
  return useMutation(usePostProductsMutationOptions(options), queryClient);
};

/**
 * Lists all products for the workspace
 */
export const useGetProductsHook = () => {
  const getProducts = usePaymentsApiHttpClient<ProductListResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getProducts({ url: `/products`, method: 'GET', signal });
    },
    [getProducts],
  );
};

export const getGetProductsQueryKey = () => {
  return [`/products`] as const;
};

export const useGetProductsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProductsQueryKey();

  const getProducts = useGetProductsHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>> = ({ signal }) =>
    getProducts(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetProductsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>>;
export type GetProductsQueryError = ErrorResponse;

export function useGetProducts<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetProducts<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetProducts<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetProducts<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetProductsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetProductsSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProductsQueryKey();

  const getProducts = useGetProductsHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>> = ({ signal }) =>
    getProducts(signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetProductsSuspenseQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>>;
export type GetProductsSuspenseQueryError = ErrorResponse;

export function useGetProductsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(
  options: {
    query: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetProductsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetProductsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetProductsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetProductsSuspenseQueryOptions(options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Gets a product by code
 */
export const useGetProductsCodeHook = () => {
  const getProductsCode = usePaymentsApiHttpClient<ProductSuccessResponse>();

  return useCallback(
    (code: string, signal?: AbortSignal) => {
      return getProductsCode({
        url: `/products/${code}`,
        method: 'GET',
        signal,
      });
    },
    [getProductsCode],
  );
};

export const getGetProductsCodeQueryKey = (code: string) => {
  return [`/products/${code}`] as const;
};

export const useGetProductsCodeQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>>;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProductsCodeQueryKey(code);

  const getProductsCode = useGetProductsCodeHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>> = ({ signal }) =>
    getProductsCode(code, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!code,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetProductsCodeQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>>;
export type GetProductsCodeQueryError = ErrorResponse;

export function useGetProductsCode<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetProductsCode<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetProductsCode<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetProductsCode<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetProductsCodeQueryOptions(code, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetProductsCodeSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProductsCodeQueryKey(code);

  const getProductsCode = useGetProductsCodeHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>> = ({ signal }) =>
    getProductsCode(code, signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetProductsCodeSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>
>;
export type GetProductsCodeSuspenseQueryError = ErrorResponse;

export function useGetProductsCodeSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetProductsCodeSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetProductsCodeSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetProductsCodeSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetProductsCodeSuspenseQueryOptions(code, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Creates a new role
 */
export const usePostRolesHook = () => {
  const postRoles = usePaymentsApiHttpClient<RoleSuccessResponse>();

  return useCallback(
    (createRoleRequest: CreateRoleRequest, signal?: AbortSignal) => {
      return postRoles({
        url: `/roles`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createRoleRequest,
        signal,
      });
    },
    [postRoles],
  );
};

export const usePostRolesMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostRolesHook>>>,
    TError,
    { data: CreateRoleRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostRolesHook>>>,
  TError,
  { data: CreateRoleRequest },
  TContext
> => {
  const mutationKey = ['postRoles'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postRoles = usePostRolesHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostRolesHook>>>,
    { data: CreateRoleRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postRoles(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostRolesMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof usePostRolesHook>>>>;
export type PostRolesMutationBody = CreateRoleRequest;
export type PostRolesMutationError = ErrorResponse;

export const usePostRoles = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostRolesHook>>>,
      TError,
      { data: CreateRoleRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostRolesHook>>>,
  TError,
  { data: CreateRoleRequest },
  TContext
> => {
  return useMutation(usePostRolesMutationOptions(options), queryClient);
};

/**
 * Lists all roles for the workspace
 */
export const useGetRolesHook = () => {
  const getRoles = usePaymentsApiHttpClient<RoleListResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getRoles({ url: `/roles`, method: 'GET', signal });
    },
    [getRoles],
  );
};

export const getGetRolesQueryKey = () => {
  return [`/roles`] as const;
};

export const useGetRolesQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRolesQueryKey();

  const getRoles = useGetRolesHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>> = ({ signal }) =>
    getRoles(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetRolesQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>>;
export type GetRolesQueryError = ErrorResponse;

export function useGetRoles<TData = Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>, TError = ErrorResponse>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetRoles<TData = Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>, TError = ErrorResponse>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetRoles<TData = Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>, TError = ErrorResponse>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetRoles<TData = Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>, TError = ErrorResponse>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetRolesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetRolesSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRolesQueryKey();

  const getRoles = useGetRolesHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>> = ({ signal }) =>
    getRoles(signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetRolesSuspenseQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>>;
export type GetRolesSuspenseQueryError = ErrorResponse;

export function useGetRolesSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>,
  TError = ErrorResponse,
>(
  options: {
    query: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetRolesSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetRolesSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetRolesSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRolesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetRolesSuspenseQueryOptions(options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Syncs an unreconciled transaction
 */
export const usePostTransactionsHook = () => {
  const postTransactions = usePaymentsApiHttpClient<TransactionSuccessResponse>();

  return useCallback(
    (syncTransactionRequest: SyncTransactionRequest, signal?: AbortSignal) => {
      return postTransactions({
        url: `/transactions`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: syncTransactionRequest,
        signal,
      });
    },
    [postTransactions],
  );
};

export const usePostTransactionsMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostTransactionsHook>>>,
    TError,
    { data: SyncTransactionRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostTransactionsHook>>>,
  TError,
  { data: SyncTransactionRequest },
  TContext
> => {
  const mutationKey = ['postTransactions'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postTransactions = usePostTransactionsHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostTransactionsHook>>>,
    { data: SyncTransactionRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postTransactions(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTransactionsMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePostTransactionsHook>>>
>;
export type PostTransactionsMutationBody = SyncTransactionRequest;
export type PostTransactionsMutationError = ErrorResponse;

export const usePostTransactions = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostTransactionsHook>>>,
      TError,
      { data: SyncTransactionRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostTransactionsHook>>>,
  TError,
  { data: SyncTransactionRequest },
  TContext
> => {
  return useMutation(usePostTransactionsMutationOptions(options), queryClient);
};

/**
 * Creates a new user
 */
export const usePostUsersHook = () => {
  const postUsers = usePaymentsApiHttpClient<UserSuccessResponse>();

  return useCallback(
    (createUserRequest: CreateUserRequest, signal?: AbortSignal) => {
      return postUsers({
        url: `/users`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createUserRequest,
        signal,
      });
    },
    [postUsers],
  );
};

export const usePostUsersMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostUsersHook>>>,
    TError,
    { data: CreateUserRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostUsersHook>>>,
  TError,
  { data: CreateUserRequest },
  TContext
> => {
  const mutationKey = ['postUsers'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postUsers = usePostUsersHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostUsersHook>>>,
    { data: CreateUserRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postUsers(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostUsersMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof usePostUsersHook>>>>;
export type PostUsersMutationBody = CreateUserRequest;
export type PostUsersMutationError = ErrorResponse;

export const usePostUsers = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostUsersHook>>>,
      TError,
      { data: CreateUserRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostUsersHook>>>,
  TError,
  { data: CreateUserRequest },
  TContext
> => {
  return useMutation(usePostUsersMutationOptions(options), queryClient);
};

/**
 * Lists all users for the workspace
 */
export const useGetUsersHook = () => {
  const getUsers = usePaymentsApiHttpClient<UserListResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getUsers({ url: `/users`, method: 'GET', signal });
    },
    [getUsers],
  );
};

export const getGetUsersQueryKey = () => {
  return [`/users`] as const;
};

export const useGetUsersQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUsersQueryKey();

  const getUsers = useGetUsersHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>> = ({ signal }) =>
    getUsers(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUsersQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>>;
export type GetUsersQueryError = ErrorResponse;

export function useGetUsers<TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError = ErrorResponse>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUsers<TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError = ErrorResponse>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUsers<TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError = ErrorResponse>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetUsers<TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError = ErrorResponse>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetUsersQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetUsersSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUsersQueryKey();

  const getUsers = useGetUsersHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>> = ({ signal }) =>
    getUsers(signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUsersSuspenseQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>>;
export type GetUsersSuspenseQueryError = ErrorResponse;

export function useGetUsersSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
  TError = ErrorResponse,
>(
  options: {
    query: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUsersSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUsersSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetUsersSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetUsersSuspenseQueryOptions(options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}
